import {
  AfterViewInit, computed, Directive, ElementRef, EventEmitter, inject, input,
  OnDestroy,
  Output
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PricingCreateFormComponent } from '../../inventory/actions/form/create/pricing-create-form/pricing-create-form.component';
import { AdminService } from '../../admin/services/admin.service';
import { InvoiceCreateFormComponent } from '../../invoice/form/create/create.component';
import { ProviderFormComponent } from '../../providers/provider-form/provider-form.component';
import { ClientCreateFormComponent } from '../../client/form/create/create.component';
import { BuyOrderCreateFormComponent } from '../../inventory/actions/form/create/buy-order-create-form/buy-order-create-form.component';
import { BudgetFormComponent } from '../../budget/form/budget-form.component';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { goToDetails } from '../../utils/navigation/go-to-details';
import { Router } from '@angular/router';

interface Element {
  id: number;
  type: string;
}

@Directive({
  selector: '[go-to-create]',
  standalone: true,
})
export class GoToCreateDirective<T extends Element> implements AfterViewInit, OnDestroy {

  elementType = input<string>();

  enableDocumentSelection = input<boolean>(false);

  queryParams = input<Record<string, unknown>>();

  openDetailsAfterClosed = input<string>(undefined);

  parent = input<T>();

  parentType = input<string>();

  _parentType = computed(() => this.parentType() || this.parent()?.type);

  confirmClose = input<boolean>(false);


  @Output()
  public afterClosed = new EventEmitter<any>();


  handler: ((event: Event) => void) = undefined;


  private readonly adminService = inject(AdminService);
  private readonly dialog = inject(MatDialog);
  private readonly domElementRef = inject(ElementRef);
  private readonly router = inject(Router);

  ngAfterViewInit(): void {
    const fn = (event: Event) => {
      event.stopPropagation();
      const config = this.buildConfig();
      this.goToCreate(config);
    }
    this.domElementRef.nativeElement.addEventListener('click', fn);

    this.handler = fn;
  }

  private buildConfig(): Record<string, unknown> {
    const config: Record<string, unknown> = {};

    config['minWidth'] = '90%';

    if (this.confirmClose()) {
      config['disableClose'] = true;
    }

    return config;
  }

  private goToCreate(config): void {

    let dialogRef: MatDialogRef<unknown>;

    switch (this.elementType()) {
      case 'Client':
        dialogRef = this.dialog.open(ClientCreateFormComponent, config);
        break;
      case 'Provider':
        dialogRef = this.dialog.open(ProviderFormComponent, config);
        break;
      case 'InventoryAction::BuyOrder':
        dialogRef = this.dialog.open(BuyOrderCreateFormComponent, { 
          data: {
            inventoryActionType: this.elementType(),
            title: this.adminService.getText('inventory_action/buy_order'),
            sourceDocument: this.parent(),
            sourceDocumentType: this._parentType(),
            enableDocumentSelection: this.enableDocumentSelection(),
          }, 
          ...config,
        });

        break;
      case 'InventoryAction::Pricing':
        dialogRef = this.dialog.open(PricingCreateFormComponent, { 
          data: {
            inventoryActionType: this.elementType(),
            title: this.adminService.getText('inventory_action/pricing'),
            sourceDocument: this.parent(),
            sourceDocumentType: this._parentType(),
            enableDocumentSelection: this.enableDocumentSelection(),
          }, 
          ...config,
        });

        break;
      case 'Invoice::Sale':
        dialogRef = this.dialog.open(InvoiceCreateFormComponent, { 
          data: {
            sourceDocument: this.parent(),
            sourceDocumentType: this._parentType(),
            enableDocumentSelection: this.enableDocumentSelection(),
          }, 
          ...config,
        });

        break;
      case 'Budget':
        dialogRef = this.dialog.open(BudgetFormComponent, {
          data: {
            sourceDocument: this.parent(),
            sourceDocumentType: this._parentType(),
          },
          ...config,
        });
        break;
      default:
        break;
    }

    dialogRef.afterClosed().subscribe((result) => {
      this.afterClosed.emit(result);

      if (result?.id && this.openDetailsAfterClosed()) {
        if (this.openDetailsAfterClosed() === 'modal') {
          goToDetails({ id: result?.id, type: this.elementType(), dialog: this.dialog });
        } else {
          console.log('goToDetails', { id: result?.id, type: this.elementType(), mode: 'route', router: this.router });
          goToDetails({ id: result?.id, type: this.elementType(),  mode: 'route', router: this.router });
        }
      }
    });

    if (this.confirmClose()) {

      dialogRef.backdropClick().subscribe(() => {
        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Cancelar acción',
            body: '¿Seguro que deseas cancelar esta acción?',
          }
        }).afterClosed().subscribe(result => {
          if (result === true) {
            dialogRef.close();
          }
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.domElementRef.nativeElement.removeEventListener('click', this.handler);
  }

}
